import Response from '../modules/classes/Response';
import { Figure, FigureCollection, FigureImage, FigureDraft, Company } from '../models/index';
import { CollectionStatus } from '../modules/Enumeration';

class FigureController {
    constructor(apiService) {
        this.apiService = apiService;
    }

    /**
     * Get distinct values for a figure metadata field (autocomplete)
     * @param {string} field - One of: category, classification, origin, character, material
     * @param {string} [q] - Optional search query
     * @param {number} [limit=20] - Max items
     * @returns {Promise<Response>} - Response containing array of strings
     */
    async getDistinctMetadataValues(field, q = '', limit = 20) {
        try {
            const response = await this.apiService.get('/figures/metadata/distinct', {
                params: { field, q, limit },
            });

            return {
                success: true,
                data: response.data,
                message: 'Metadata values fetched successfully',
                statusCode: 200,
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                message: error.message || 'Failed to fetch metadata values',
                error,
            };
        }
    }

    /**
     * Get a single figure by ID
     * @param {string} id - Figure ID
     * @returns {Promise<Response>} - Response containing figure data
     */
    async getFigure(id) {
        try {
            const response = await this.apiService.get(`/figures/${id}`);
            const figureData = response.data;
            
            if (figureData) {
                return new Response({
                    success: true,
                    data: Figure.fromAPI(figureData),
                    message: 'Figure fetched successfully',
                });
            }
            
            return new Response({
                success: false,
                message: 'Figure not found',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch figure',
                error,
            });
        }
    }

    /**
     * Get all figures with optional filters
     * @param {Object} filters - Filter options
     * @returns {Promise<Response>} - Response containing array of figures
     */
    async getFigures(filters = {}) {
        try {
            const response = await this.apiService.get('/figures', { params: filters });
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch figures',
                error,
            });
        }
    }

    async getLatestReleases(limit = 10) {
        try {
            const response = await this.apiService.get('/figure-database/latest-releases/combined', {
                params: { limit }
            });

            
            // Handle nested response structure
            // response.data is already the data object containing { data, count, breakdown }
            const responseData = response.data || {};
            const { data: releasesData = [], count = 0, breakdown = {} } = responseData;
            
            if (Array.isArray(releasesData)) {
                const releases = releasesData.map(entry => {
                    if (entry.figure) {
                        const fig = Figure.fromAPI(entry.figure);
                        return fig;
                    }
                    return null;
                }).filter(Boolean);
                
                
                return new Response({
                    success: true,
                    data: releases,
                    meta: { count, breakdown },
                    message: releases.length > 0 ? 'Latest releases fetched successfully' : 'No latest releases found',
                });
            }
            
            return new Response({
                success: true,
                data: [],
                meta: { count: 0, breakdown: { byReleaseDate: 0, byAddedDate: 0 } },
                message: 'No latest releases found',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch latest releases',
                error,
            });
        }
    }

    /**
     * Add a figure to a user's collection
     * @param {string} figureId - Figure ID
     * @param {CollectionStatus} status - Collection status (collected, wished, ordered)
     * @returns {Promise<Response>} - Response containing the updated collection
     */
    async addToCollection(figureId, status = CollectionStatus.COLLECTED) {
        try {
            const response = await this.apiService.post(`/figures/${figureId}/collection`, {
                collection: status
            });
            
            const collectionData = response.data;
            
            if (collectionData) {
                return new Response({
                    success: true,
                    data: FigureCollection.fromAPI(collectionData),
                    message: 'Figure added to collection',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to add figure to collection',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to add figure to collection',
                error,
            });
        }
    }

    /**
     * Remove a figure from user's collection
     * @param {string} figureId - Figure ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async removeFromCollection(figureId) {
        try {
            await this.apiService.delete(`/figures/${figureId}/collection`);
            
            return new Response({
                success: true,
                message: 'Figure removed from collection',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to remove figure from collection',
                error,
            });
        }
    }

    /**
     * Set a figure as grail
     * @param {string} figureId - Figure ID
     * @param {number} rank - Grail rank (1 being the highest)
     * @returns {Promise<Response>} - Response containing the updated collection
     */
    async setAsGrail(figureId, rank = 1) {
        try {
            const response = await this.apiService.post(`/figures/${figureId}/grail`, {
                grailRank: rank
            });
            
            const collectionData = response.data;
            
            if (collectionData) {
                return new Response({
                    success: true,
                    data: FigureCollection.fromAPI(collectionData),
                    message: 'Figure set as grail',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to set figure as grail',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to set figure as grail',
                error,
            });
        }
    }

    /**
     * Remove grail status from figure
     * @param {string} figureId - Figure ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async removeGrailStatus(figureId) {
        try {
            await this.apiService.delete(`/figures/${figureId}/grail`);
            
            return new Response({
                success: true,
                message: 'Grail status removed',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to remove grail status',
                error,
            });
        }
    }

    /**
     * Get user's figure collection
     * @param {string} userId - Optional user ID (defaults to current user)
     * @returns {Promise<Response>} - Response containing figures in collection
     */
    async getUserCollection(userId = null) {
        try {
            const endpoint = userId 
                ? `/users/${userId}/collection` 
                : '/me/collection';
                
            const response = await this.apiService.get(endpoint);
            const collectionData = response.data;
            
            if (collectionData && Array.isArray(collectionData)) {
                const collection = collectionData.map(item => FigureCollection.fromAPI(item));
                
                return new Response({
                    success: true,
                    data: collection,
                    message: 'Collection fetched successfully',
                });
            }
            
            return new Response({
                success: false,
                message: 'Failed to fetch collection',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch collection',
                error,
            });
        }
    }

    /**
     * Upload a figure image
     * @param {string} figureId - Figure ID
     * @param {Object} imageData - Image data (base64 or uri)
     * @returns {Promise<Response>} - Response containing the created figure image
     */
    async uploadFigureImage(figureId, imageData) {
        try {
            const formData = new FormData();
            
            if (typeof imageData === 'string') {
                // If it's a string URI
                const uri = imageData;
                const name = uri.split('/').pop();
                const match = /\.(\w+)$/.exec(name);
                const type = match ? `image/${match[1]}` : 'image/jpeg';
                
                formData.append('image', {
                    uri,
                    name,
                    type
                });
            } else {
                // Assume it's already a proper object
                formData.append('image', imageData);
            }
            
            const response = await this.apiService.post(
                `/figures/${figureId}/images`, 
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            
            const imageResponseData = response.data;
            
            if (imageResponseData) {
                return new Response({
                    success: true,
                    data: FigureImage.fromAPI(imageResponseData),
                    message: 'Image uploaded successfully',
                });
            }
            return new Response({
                success: false,
                message: 'Failed to upload image',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to upload image',
                error,
            });
        }
    }

    /**
     * Create a new figure
     * @param {FormData} formData - Figure data as FormData
     * @returns {Promise<Response>} - Response containing the created figure
     */
    async createFigure(formData) {
        try {
            const response = await this.apiService.post('/figures', formData);
            const figureData = response.data;

            if (figureData) {
                return new Response({
                    success: true,
                    data: Figure.fromAPI(figureData),
                    message: 'Figure created successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to create figure',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to create figure',
                error,
            });
        }
    }

    /**
     * Create a new figure draft
     * @param {Object} figureData - Figure draft data
     * @returns {Promise<Response>} - Response containing the created figure draft
     */
    async createFigureDraft(figureData) {
        try {
            const response = await this.apiService.post('/figures/drafts', figureData);
            const draftData = response.data;

            if (draftData) {
                return new Response({
                    success: true,
                    data: FigureDraft.fromAPI(draftData),
                    message: 'Figure draft created successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to create figure draft',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to create figure draft',
                error,
            });
        }
    }

    /**
     * Submit a figure draft for approval
     * @param {string} draftId - Draft ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async submitFigureDraft(draftId) {
        try {
            const response = await this.apiService.post(`/figures/drafts/${draftId}/submit`);
            const draftData = response.data;

            if (draftData) {
                return new Response({
                    success: true,
                    data: FigureDraft.fromAPI(draftData),
                    message: 'Figure draft submitted successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to submit figure draft',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to submit figure draft',
                error,
            });
        }
    }

    /**
     * Get companies list
     * @returns {Promise<Response>} - Response containing companies
     */
    async getCompanies() {
        try {
            const response = await this.apiService.get('/companies');
            const companiesData = response.data;

            if (companiesData && Array.isArray(companiesData)) {
                const companies = companiesData.map(companyData => Company.fromAPI(companyData));

                return new Response({
                    success: true,
                    data: companies,
                    message: 'Companies fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'Failed to fetch companies',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch companies',
                error,
            });
        }
    }

    async getFigureDrafts(filters = { skip: 1, take: 10 }) {
        try {
            const response = await this.apiService.requestWithParams('/admin/figures/drafts/', 'GET', filters);
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch figure drafts',
                error,
            });
        }
    }

    async getPendingRejectsFigures() {
        try {
            const response = await this.apiService.get('/figures/status/pending-rejects');
            const figuresData = response.data;

            if (figuresData && Array.isArray(figuresData)) {
                const figures = figuresData.map(item => Figure.fromAPI(item));

                return new Response({
                    success: true,
                    data: figures,
                    message: 'Pending figures fetched successfully',
                });
            }

            return new Response({
                success: false,
                message: 'No pending figures found',
            });
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to fetch pending figures',
                error,
            });
        }
    }

    /**
     * Approve a figure draft
     * @param {string} figureId - Figure ID
     * @returns {Promise<Response>} - Response indicating success
     */
    async approveFigure(figureId) {
        try {
            const response = await this.apiService.patch(`/admin/figures/${figureId}/approve`);
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to approve figure',
                error,
            });
        }
    }

    /**
     * Reject a figure draft
     * @param {string} figureId - Figure ID
     * @param {string} reason - Rejection reason
     * @returns {Promise<Response>} - Response indicating success
     */
    async rejectFigure(figureId, reason) {
        try {
            const response = await this.apiService.patch(`/admin/figures/${figureId}/reject`, {
                reason: reason
            });
            return Response.parse(response);
        } catch (error) {
            return new Response({
                success: false,
                message: error.message || 'Failed to reject figure',
                error,
            });
        }
    }
}

// Export a default controller instance
const defaultFigureController = new FigureController();
export { defaultFigureController };
export default FigureController;